// ─── Feuille : créer / éditer un COMPTE bancaire ──────────────────
// Nom choisi, type, banque, 4 derniers chiffres (pour le reconnaître d'un coup
// d'œil), et solde de départ à une date : c'est lui qui permet de vérifier que
// l'import est complet — si le solde calculé diffère de celui de la banque, il
// manque des opérations.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, stylesForm, frJMA, useUI, useT } from '@mop/core';
import { TYPES_COMPTE, emojiType } from './services/comptes';

const aujourdhui = () => new Date().toISOString().slice(0, 10);
const VIDE = { nom: '', type: 'courant', banque: '', fin4: '', soldeInitial: '', dateSolde: '' };

export default function FeuilleCompte({ visible, initial, onDemanderDate, onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);

  useEffect(() => {
    if (!visible) return;
    const c = initial || {};
    setF({
      ...VIDE, ...c,
      soldeInitial: c.soldeInitial != null ? String(c.soldeInitial) : '',
      dateSolde: c.dateSolde || aujourdhui(),
    });
  }, [visible, initial]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const valide = !!(f.nom || '').trim();

  return (
    <Feuille visible={visible} titre={initial?.id ? t('finances.modifierCompte') : t('finances.nouveauCompte')}
      emoji={emojiType(f.type)} hauteur={0.8} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide,
        onPress: () => onEnregistrer(f) }}>

      <Text style={s.lbl}>{t('commun.nom')}</Text>
      <TextInput style={s.input} value={f.nom} onChangeText={v => set('nom', v)}
        placeholder={t('finances.comptePh')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('finances.typeCompte')}</Text>
      <ChoixPuces options={TYPES_COMPTE.map(x => ({ id: x.id, label: `${x.emoji} ${t(`finances.cpt_${x.id}`)}` }))}
        value={f.type} onChange={v => set('type', v)} />

      <View style={s.row}>
        <View style={{ flex: 2 }}>
          <Text style={s.lbl}>{t('finances.banque')}</Text>
          <TextInput style={s.input} value={f.banque} onChangeText={v => set('banque', v)}
            placeholder="LCL, BNP…" placeholderTextColor={UI.faint} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('finances.fin4')}</Text>
          <TextInput style={s.input} value={f.fin4} onChangeText={v => set('fin4', v.replace(/\D/g, '').slice(0, 4))}
            keyboardType="number-pad" placeholder="1234" placeholderTextColor={UI.faint} />
        </View>
      </View>

      <Text style={s.lbl}>{t('finances.soldeDepart')}</Text>
      <TextInput style={[s.input, s.montant]} value={f.soldeInitial}
        onChangeText={v => set('soldeInitial', v.replace(/[^0-9.,-]/g, ''))}
        keyboardType="numbers-and-punctuation" placeholder="0,00" placeholderTextColor={UI.faint} />
      <Text style={s.aide}>{t('finances.soldeDepartAide')}</Text>

      <Text style={s.lbl}>{t('finances.dateSolde')}</Text>
      <View style={s.date}>
        <Text style={{ fontSize: 15 }}>📅</Text>
        <Text style={s.dateTxt} onPress={() => onDemanderDate?.({ valeur: f.dateSolde, onChoisir: (dk) => set('dateSolde', dk) })}>
          {f.dateSolde ? frJMA(f.dateSolde) : t('commun.choisirDate')}
        </Text>
      </View>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  row: { flexDirection: 'row', gap: 8 },
  montant: { fontSize: 19, fontWeight: '800', textAlign: 'right' },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 5 },
  date: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 12, paddingHorizontal: 12 },
  dateTxt: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
});
